import type { ApplicationDraft } from "@shared/widgets/yaas/types";
import type { PublishedTermsDescriptor, RetailWalletChallengeResponse } from "@solomon-apps/shared";

import { readVerificationAnalytics, type VerificationAnalyticsRecord } from "@/services/analytics/enrollment";

import { isRecord, isWalletChallenge } from "./verificationResponse";

function isTermsDigest(value: unknown): value is PublishedTermsDescriptor["contentDigest"] {
    return typeof value === "string" && /^sha256:[a-f0-9]{64}$/.test(value);
}

function readStoredTerms(value: unknown): PublishedTermsDescriptor | null {
    if (
        !isRecord(value) ||
        (value.termsUuid !== null && typeof value.termsUuid !== "string") ||
        typeof value.name !== "string" ||
        typeof value.documentKey !== "string" ||
        typeof value.version !== "string" ||
        typeof value.contentFormat !== "string" ||
        typeof value.content !== "string" ||
        !isTermsDigest(value.contentDigest)
    )
        return null;
    return {
        termsUuid: value.termsUuid,
        name: value.name,
        documentKey: value.documentKey,
        version: value.version,
        contentFormat: value.contentFormat,
        content: value.content,
        contentDigest: value.contentDigest
    };
}

/** Keep the payload-bound challenge together with the paid proof across retries/refresh. */
export interface PendingEnrollmentVerification {
    analytics?: VerificationAnalyticsRecord;
    walletAddress: string;
    draft: ApplicationDraft;
    challenge: RetailWalletChallengeResponse;
    transactionSignature?: string;
}

// A null entry also records a clear when browser storage could not be updated.
const memory = new Map<string, PendingEnrollmentVerification | null>();
const key = (address: string): string => `yaas-pending-enrollment:${address}`;

export function savePendingEnrollment(attempt: PendingEnrollmentVerification): void {
    memory.set(attempt.walletAddress, attempt);
    try {
        window.sessionStorage.setItem(key(attempt.walletAddress), JSON.stringify(attempt));
    } catch {
        console.warn("Could not persist verification recovery. Keep this page open until enrollment completes.");
    }
}

export function clearPendingEnrollment(address: string): void {
    memory.set(address, null);
    try {
        window.sessionStorage.removeItem(key(address));
    } catch {
        console.warn("Could not clear stored verification recovery. It remains cleared for this page session.");
    }
}

export function readPendingEnrollment(address: string): PendingEnrollmentVerification | null {
    if (memory.has(address)) return memory.get(address) ?? null;
    let raw: string | null;
    try {
        raw = window.sessionStorage.getItem(key(address));
    } catch {
        console.warn("Could not read stored verification recovery. Recovery is available only for this page session.");
        return null;
    }
    if (!raw) return null;
    let value: unknown;
    try {
        value = JSON.parse(raw);
    } catch {
        console.warn("Stored verification recovery is invalid and cannot be resumed.");
        return null;
    }
    if (!isRecord(value)) return null;
    const { draft, challenge, transactionSignature } = value;
    if (
        value.walletAddress !== address ||
        !isRecord(draft) ||
        (draft.distributionMode !== "stream" && draft.distributionMode !== "claim") ||
        typeof draft.rewardsDestination !== "string" ||
        typeof draft.accountLabel !== "string" ||
        draft.tosAccepted !== true ||
        !isWalletChallenge(challenge) ||
        (transactionSignature !== undefined && typeof transactionSignature !== "string")
    )
        return null;
    const terms = readStoredTerms(draft.terms);
    if (!terms) return null;
    const attempt: PendingEnrollmentVerification = {
        analytics: readVerificationAnalytics(value.analytics),
        walletAddress: address,
        draft: {
            distributionMode: draft.distributionMode,
            rewardsDestination: draft.rewardsDestination,
            accountLabel: draft.accountLabel,
            tosAccepted: draft.tosAccepted,
            terms
        },
        challenge: {
            nonce: challenge.nonce,
            message: challenge.message,
            expiresAt: challenge.expiresAt,
            ...(challenge.microTransfer
                ? {
                      microTransfer: {
                          recipient: challenge.microTransfer.recipient,
                          lamports: challenge.microTransfer.lamports,
                          memo: challenge.microTransfer.memo
                      }
                  }
                : {})
        },
        ...(transactionSignature !== undefined ? { transactionSignature } : {})
    };
    memory.set(address, attempt);
    return attempt;
}
